import React, { useState, useEffect } from "react";
import axios from "axios";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Auth, { API_URL } from "../../../../utils/auth";
import { toast } from 'react-toastify';
import "./CircleManagement.css";

const MIN_SCORE = 1;
const MAX_SCORE = 10;

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff', 
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(87, 42, 241, 1)', 
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#fff', 
      border: '6px solid rgba(87, 42, 241, 0.3)', 
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    backgroundColor: '#fff', 
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const CircleManagement = ({ onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  
  const [viewMode, setViewMode] = useState("menu");
  const [crsEnabled, setCrsEnabled] = useState(false);
  const [isCrsLoading, setIsCrsLoading] = useState(true);

  useEffect(() => {
    loadCRSSetting();
  }, []);

  const saveCircleData = async () => {
    if (clients.length === 0) {
      toast.warn("No client data to save");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const user = Auth.getUser();
      const workspaceId = Auth.getWorkspaceId();

      if (!user || !workspaceId) {
        setError("User or workspace information not found");
        setIsSaving(false);
        return;
      }

      const circleData = clients.map(client => ({
        clientId: client.id,
        score: client.activityScore || 0
      }));

      const payload = {
        workspaceId: parseInt(workspaceId),
        adminUserId: parseInt(user.id),
        circleData: circleData
      };

      const response = await axios.post(`${API_URL}/circle/save-circle-data`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Circle activity scores saved successfully!');
      } else {
        setError(response.data?.message || "Failed to save circle data");
        toast.error("Failed to save circle activity scores");
      }
    } catch (error) {
      if (error.response) {
        setError(error.response.data?.message || "Failed to save circle data");
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Something went wrong while saving. Please try again.");
      }
      toast.error("Failed to save circle activity scores. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getCustomerActivityScore = async () => {
    setIsLoading(true);
    setError("");
    setClients([]);
    setSummary(null);

    try {
      const user = Auth.getUser();
      const workspaceId = Auth.getWorkspaceId();

      if (!user || !workspaceId) {
        setError("User or workspace information not found");
        setIsLoading(false);
        return;
      }

      let url = `${API_URL}/circle/circle-clients?workspaceId=${workspaceId}&adminUserId=${user.id}`;

      const response = await axios.get(url);

      if (response.status === 200) {
        toast.success('Activity scores are being processed and will be available soon!');
        
        setSummary({ 
          processing: true,
          totalClients: response.data.summary?.totalClients || 0
        });
      } else {
        setError(response.data?.message || "Failed to start processing activity scores");
        toast.error("Failed to start processing activity scores");
      }
    } catch (error) {
      if (error.response) {
        setError(error.response.data?.message || "Failed to start processing activity scores");
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      toast.error("Failed to start processing activity scores. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterTypeChange = (newFilterType) => {
    setClients([]);
    setSummary(null);
    setError("");
  };

  const handleBack = () => {
    setClients([]);
    setSummary(null);
    setError("");
    setIsSaving(false);
    onBack();
  };

  const handleBackToMenu = () => {
    setViewMode("menu");
    setClients([]);
    setSummary(null);
    setError("");
    setIsSaving(false);
  };

  const handleGoToActivityScores = () => {
    setViewMode("activityScores");
    getCustomerActivityScore();
  };

  const loadCRSSetting = async () => {
    try {
      setIsCrsLoading(true);
      const workspaceId = Auth.getWorkspaceId();
      if (!workspaceId) {
        console.error('Workspace ID not found');
        setIsCrsLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/admin/crs-settings`, {
        params: { workspaceId },
      });

      if (response.data && response.data.length > 0) {
        const circleIntegrationSetting = response.data.find(setting => setting.slug === 'circle');
        setCrsEnabled(circleIntegrationSetting ? circleIntegrationSetting.enabled : false);
      } else {
        setCrsEnabled(false);
      }
    } catch (error) {
      console.error('Error loading CRS setting:', error);
      setCrsEnabled(false);
    } finally {
      setIsCrsLoading(false);
    }
  };

  const handleCRSToggle = async (event) => {
    const { checked } = event.target;
    const workspaceId = Auth.getWorkspaceId();
    
    if (!workspaceId) {
      console.error('Workspace ID is missing!');
      return;
    }

    try {
      setCrsEnabled(checked);

      const putData = {
        workspaceId: workspaceId,
        slug: 'circle',
        enabled: checked
      };

      await axios.put(`${API_URL}/admin/crs-settings`, putData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      toast.success(checked ? 'Circle.so enabled for CRS calculation' : 'Circle.so disabled for CRS calculation');

    } catch (error) {
      console.error('Error updating CRS setting:', error);
      setCrsEnabled(!checked);
      toast.error('Failed to update CRS setting. Please try again.');
    }
  };

  return (
    <div className="circle-management-modal">
      <div className="modal-header">
        <h2>
          {viewMode === "activityScores" 
            ? "Customer Activity Scores" 
            : "Circle.so Management"}
        </h2>
        <button 
          className="modal-close" 
          onClick={viewMode === "menu" ? handleBack : handleBackToMenu}
        >
          ×
        </button>
      </div>
      
      <div className="modal-body">
        {viewMode === "menu" ? (
          <div className="menu-section">
            <h3>Select an Option</h3>
            <p>Choose what you want to do with your Circle.so integration</p>
            
            <div className="menu-options">
              <div className="menu-option-card" onClick={handleGoToActivityScores}>
                <div className="option-icon">📊</div>
                <h4>Get Activity Scores</h4>
                <p>Retrieve and save the latest customer activity scores from Circle.so</p>
                <button className="option-button">Get Scores</button>
              </div>
            </div>
            
            <div style={{ marginTop: '40px' }}>
              <div className="crs-toggle-section">
                {isCrsLoading ? (
                  <div className="crs-loading">
                    <div className="loading-spinner" style={{width: '16px', height: '16px', marginRight: '8px'}}></div>
                    <span>Loading CRS setting...</span>
                  </div>
                ) : (
                  <div className="crs-toggle-container">
                    <IOSSwitch
                      checked={crsEnabled}
                      onChange={handleCRSToggle}
                      name="crsEnabled"
                    />
                    <span className="crs-toggle-label">Enable for CRS calculation</span>
                  </div>
                )}
                <p className="crs-toggle-description">
                  When enabled, Circle.so activity scores will be included in Client Retention Score calculations for applicable clients.
                </p>
              </div>
            </div>
          </div>
        ) : (

          <div className="activity-scores-view">
            <div className="management-section">
          <h3>Customer Activity Scores</h3>
          <p>Process the latest activity scores for all your customers from Circle.so using our queue system.</p>

          {isLoading && (
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <p>Starting activity scores processing...</p>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {summary && summary.processing && (
            <div className="processing-section">
              <div className="processing-message">
                <div className="processing-icon">⏳</div>
                <h4>Activity Scores Processing</h4>
                <p>Your activity scores are being processed in the background and will be available soon. This may take a few minutes depending on the number of clients.</p>
                {summary.totalClients > 0 && (
                  <div className="total-clients-info">
                    <span className="total-clients-label">Total Clients:</span>
                    <span className="total-clients-value">{summary.totalClients}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
            </div>
        )}
      </div>
      
      <div className="modal-footer">
        <button 
          type="button" 
          onClick={viewMode === "menu" ? handleBack : handleBackToMenu} 
          className="close-button"
        >
          {viewMode === "menu" ? "Close" : "Back to Menu"}
        </button>
      </div>
    </div>
  );
};

export default CircleManagement;
